import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { createApiAsyncThunk } from '@/services/apiService'
import { setTimeoutInDev } from '@/utils/environment'
import { addFlashMessage } from '../flash/flashMessagesSlice'
import { sanitizeFormData } from '@/utils/formData'

const URL = 'visites'
const getUrl = (bien_id, id) => {
  const url = `biens/${bien_id}/${URL}`
  if(id) {
    return `${url}/${id}`
  }
  return url
}

// États initiaux
const initialState = {
  items: [],
  current: null,
  loadingStatus: {
    fetchList: false,
    fetchItem: false,
    create: false,
    update: false,
    delete: false,
  },
  requestStatus: {
    success: false,
    error: null,
    errors: {}
  },
  filters: {
    page: 1,
    limit: 10
  },
  pagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  },
}

const sanitizeVisiteFormData = (data) => {
  const formData = sanitizeFormData(data)
  delete formData.bien_id
  return formData
}

// Thunks
export const fetchVisites = createApiAsyncThunk(
  'visites/fetchVisites',
  async (params = {}, { api, dispatch, getState, rejectWithValue }) => {
    try {
      await setTimeoutInDev()
      let url = URL
      if (params.bien_id) {
        url = `biens/${params.bien_id}/visites`
        delete params.bien_id
      }
      const response = await api
        .url(url)
        .query(params)
        .get()
      return response
    } catch (error) {
      console.error('Error fetching visites:', error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération des visites')
    }
  }
)

export const createVisite = createApiAsyncThunk(
  'visites/createVisite',
  async (params, { api, dispatch, rejectWithValue }) => {
    const { bien_id, ...data} = params
    try {
      if(!bien_id) throw new Error("Une visite doit être attachée à un bien")

      const response = await api
        .url(getUrl(bien_id))
        .post({visite: data})
      dispatch(addFlashMessage({type: 'success', text: "Visite créée avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const updateVisite = createApiAsyncThunk(
  'visites/updateVisite',
  async ({ bien_id, id, data }, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(getUrl(bien_id, id))
        .put({visite: sanitizeVisiteFormData(data)})
      dispatch(addFlashMessage({type: 'success', text: "Visite mise à jour avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const deleteVisite = createApiAsyncThunk(
  'visites/deleteVisite',
  async (visite, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(getUrl(visite.bien_id, visite.id))
        .delete()
      dispatch(addFlashMessage({type: 'success', text: "Visite a été supprimé avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

// Valeurs par défaut pour un bien vide
export const emptyVisite = {
  bien_id: null,
  date_visite: '',
  commentaires: ''
}

const sanitizesPayload = (action) => Object.fromEntries(
  Object.entries(action.payload).map(([key, value]) =>
    [key, value === null ? (emptyVisite[key] || "") : value]
  )
)

// Slice
const visitesSlice = createSlice({
  name: 'visites',
  initialState,
  reducers: {
    setEmptyCurrentVisite: (state, action) => {
      const data = {
        ...emptyVisite,
        ...action.payload
      }
      state.current = {
        ...data
      }
      state.requestStatus = initialState.requestStatus
    },
    setCurrentVisite: (state, action) => {
      const sanitizedData = {
        ...emptyVisite,
        ...sanitizesPayload(action)
      }
      state.current = {
        ...sanitizedData
      }
      state.requestStatus = initialState.requestStatus
    },
    resetCurrentVisite: (state) => {
      state.current = initialState.current
      state.requestStatus = initialState.requestStatus
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetRequestStatus: (state) => {
      state.requestStatus = initialState.requestStatus
    },
  },
  extraReducers: (builder) => {
    // fetchVisites
    builder.addCase(fetchVisites.pending, (state) => {
      state.loadingStatus.fetchList = true
      state.requestStatus.error = null
    })
    builder.addCase(fetchVisites.fulfilled, (state, action) => {
      state.items = action.payload.visites
      state.pagination = {
        totalCount: action.payload.total_count,
        totalPages: action.payload.total_pages,
        currentPage: action.payload.current_page
      }
      state.loadingStatus.fetchList = false
      state.requestStatus.success = true
    })
    builder.addCase(fetchVisites.rejected, (state, action) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.error = action.payload
    })

    // createVisite
    builder.addCase(createVisite.pending, (state) => {
      state.loadingStatus.create = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(createVisite.fulfilled, (state, action) => {
      state.items.unshift(action.payload)
      state.loadingStatus.create = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(createVisite.rejected, (state, action) => {
      state.loadingStatus.create = false
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.error = action.payload
      state.requestStatus.success = false
    })

    // updateVisite
    builder.addCase(updateVisite.pending, (state) => {
      state.loadingStatus.update = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(updateVisite.fulfilled, (state, action) => {
      const updated = action.payload
      const index = state.items.findIndex(item => item.id === updated.id)
      if (index !== -1) {
        state.items[index] = updated
      }
      state.loadingStatus.update = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(updateVisite.rejected, (state, action) => {
      state.loadingStatus.update = false
      state.requestStatus.error = action.payload
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.success = false
    })
 
    // Delete Visite
    builder.addCase(deleteVisite.pending, (state) => {
      state.loadingStatus.delete = true
      state.requestStatus.error = null
    })
    builder.addCase(deleteVisite.fulfilled, (state, action) => {
      state.loadingStatus.delete = false
      const index = state.items.findIndex( item => item.id === action.payload.id)
      if (index !== -1) {
        state.items.splice(index, 1)
      }
      state.requestStatus.success = true
    })
    builder.addCase(deleteVisite.rejected, (state, action) => {
      state.loadingStatus.delete = false
      state.requestStatus.error = action.payload
    })
  }
})

export const { 
  setEmptyCurrentVisite,
  setCurrentVisite,
  resetCurrentVisite,
  setFilters,
  resetRequestStatus,
} = visitesSlice.actions

export default visitesSlice.reducer